import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { locationSocket } from '@/api/client/socket';
import { useAuth } from '@/providers/AuthProvider';

import { UserRole } from '@/types/enums';
import api from '@/api/client/axios';
import { getCurrentLocations } from '@/api/locations';
import { CurrentLocationDetail } from '@packages/database/types';

interface CrewLocationPoint {
  userId: string;
  organizationId: string;
  latitude: number;
  longitude: number;
  heading?: number | null;
  speed?: number | null;
  accuracy?: number | null;
  updatedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export const useCrewLocations = () => {
  const { user, organization } = useAuth();
  const orgId = organization?.id!;

  // Admin only
  const isAdmin = user?.role === UserRole.ADMIN;

  const query = useQuery({
    queryKey: ['locations', 'current', orgId],
    queryFn: () => getCurrentLocations(),
    enabled: isAdmin && !!orgId,
    staleTime: 15_000, // 15s — WebSocket keeps it fresh in real time
    refetchInterval: 30_000, // 30s fallback poll if WebSocket drops
  });

  // ── Live updates via WebSocket
  const [liveLocations, setLiveLocations] = useState(new Map());

  // Seed live map from initial query data
  useEffect(() => {
    if (!query.data) return;
    setLiveLocations(new Map(query.data.map((loc) => [loc.userId, loc])));
  }, [query.data]);

  useEffect(() => {
    if (!isAdmin) return;

    // location_updated — crew member moved
    const handleUpdate = (
      data: Partial<CrewLocationPoint> & { userId: string },
    ) => {
      console.log('[CrewLocations] Got update:', JSON.stringify(data));
      setLiveLocations((prev) => {
        const next = new Map(prev);
        const current = next.get(data.userId);

        next.set(data.userId, { ...current, ...data } as CrewLocationPoint);
        return next;
      });
    };

    // user_offline — crew member disconnected or ended shift
    const handleOffline = ({ userId }: { userId: string }) => {
      setLiveLocations((prev) => {
        const next = new Map(prev);
        next.delete(userId);
        return next;
      });
    };

    locationSocket.on('location_updated', handleUpdate);
    locationSocket.on('user_offline', handleOffline);

    return () => {
      locationSocket.off('location_updated', handleUpdate);
      locationSocket.off('user_offline', handleOffline);
    };
  }, [isAdmin]);

  return {
    locations: Array.from(liveLocations.values()).sort((a, b) =>
      a.user.name.localeCompare(b.user.name),
    ),
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
